/**
 * Create Account Page
 *
 * Multi-step wizard for creating new tenant accounts.
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@respectly/ui';
import { cn } from '@respectly/ui';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CreditCard,
  User,
  Check,
  Loader2,
} from 'lucide-react';
import { useAccountsStore } from '../../stores/useAccountsStore';

// ============================================
// Types
// ============================================

interface FormData {
  // Step 1: Basic Info
  name: string;
  slug: string;
  industry: string;
  source: string;
  // Step 2: Plan Selection
  planId: string;
  // Step 3: Admin User
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  skipWelcomeEmail: boolean;
  // Step 4: Notes
  notes: string;
}

const initialFormData: FormData = {
  name: '',
  slug: '',
  industry: '',
  source: 'sales',
  planId: '',
  adminEmail: '',
  adminFirstName: '',
  adminLastName: '',
  skipWelcomeEmail: false,
  notes: '',
};

// Mock plans - would come from API
const mockPlans = [
  { id: 'plan-1', name: 'Starter', price: 49 },
  { id: 'plan-2', name: 'Professional', price: 99 },
  { id: 'plan-3', name: 'Enterprise', price: 299 },
];

const industries = [
  'Healthcare',
  'Finance',
  'Real Estate',
  'Legal',
  'Insurance',
  'Education',
  'Technology',
  'Other',
];

const sources = ['signup', 'referral', 'sales', 'import'];

// ============================================
// Steps Configuration
// ============================================

const steps = [
  { id: 1, title: 'Basic Info', icon: Building2 },
  { id: 2, title: 'Select Plan', icon: CreditCard },
  { id: 3, title: 'Admin User', icon: User },
  { id: 4, title: 'Review', icon: Check },
];

// ============================================
// Component
// ============================================

export function CreateAccount() {
  const navigate = useNavigate();
  const { createAccount, isPerformingAction } = useAccountsStore();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Handle input changes
  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is edited
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }

    // Auto-generate slug from name
    if (field === 'name' && typeof value === 'string') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  // Validate current step
  const validateStep = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (currentStep === 1) {
      if (!formData.name.trim()) newErrors.name = 'Account name is required';
      if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
      else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
        newErrors.slug = 'Slug must be lowercase letters, numbers, and hyphens only';
      }
    }

    if (currentStep === 2) {
      if (!formData.planId) newErrors.planId = 'Please select a plan';
    }

    if (currentStep === 3) {
      if (!formData.adminEmail.trim()) newErrors.adminEmail = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
        newErrors.adminEmail = 'Please enter a valid email';
      }
      if (!formData.adminFirstName.trim()) newErrors.adminFirstName = 'First name is required';
      if (!formData.adminLastName.trim()) newErrors.adminLastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  // Handle previous step
  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateStep()) return;

    const accountId = await createAccount({
      name: formData.name,
      slug: formData.slug,
      planId: formData.planId,
      industry: formData.industry || undefined,
      source: (formData.source as any) || undefined,
      notes: formData.notes || undefined,
      adminEmail: formData.adminEmail,
      adminFirstName: formData.adminFirstName,
      adminLastName: formData.adminLastName,
      skipWelcomeEmail: formData.skipWelcomeEmail,
    });

    if (accountId) {
      navigate({ to: '/accounts/$id', params: { id: accountId } });
    }
  };

  // Get selected plan
  const selectedPlan = mockPlans.find((p) => p.id === formData.planId);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: '/accounts' })}
          className="-ml-2 mb-4 text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to Accounts
        </Button>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
          Create Account
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Set up a new tenant account with an initial admin user.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;

            return (
              <div key={step.id} className="flex items-center">
                {/* Step Circle */}
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                    isCompleted
                      ? 'border-emerald-500 bg-emerald-500 text-white'
                      : isActive
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-slate-300 bg-white text-slate-400 dark:border-slate-600 dark:bg-slate-800'
                  )}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>

                {/* Step Label (hidden on mobile) */}
                <span
                  className={cn(
                    'ml-2 hidden text-sm font-medium sm:block',
                    isActive
                      ? 'text-slate-900 dark:text-white'
                      : 'text-slate-500'
                  )}
                >
                  {step.title}
                </span>

                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      'mx-4 h-0.5 w-12 sm:w-16',
                      isCompleted ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Form Card */}
      <Card>
        <CardContent className="p-6">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Account Name *</Label>
                <Input
                  id="name"
                  placeholder="Acme Corporation"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Slug *</Label>
                <div className="flex items-center">
                  <span className="text-sm text-slate-500 mr-1">/</span>
                  <Input
                    id="slug"
                    placeholder="acme-corp"
                    value={formData.slug}
                    onChange={(e) => handleChange('slug', e.target.value)}
                    className={cn('font-mono', errors.slug ? 'border-red-500' : '')}
                  />
                </div>
                {errors.slug && (
                  <p className="text-xs text-red-500">{errors.slug}</p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => handleChange('industry', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((industry) => (
                        <SelectItem key={industry} value={industry}>
                          {industry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source">Source</Label>
                  <Select
                    value={formData.source}
                    onValueChange={(value) => handleChange('source', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {sources.map((source) => (
                        <SelectItem key={source} value={source} className="capitalize">
                          {source}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Plan Selection */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500 mb-4">
                Select a subscription plan for this account.
              </p>
              <div className="grid gap-3">
                {mockPlans.map((plan) => (
                  <div
                    key={plan.id}
                    onClick={() => handleChange('planId', plan.id)}
                    className={cn(
                      'flex items-center justify-between rounded-lg border-2 p-4 cursor-pointer transition-colors',
                      formData.planId === plan.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-slate-200 hover:border-slate-300 dark:border-slate-700'
                    )}
                  >
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">
                        {plan.name}
                      </p>
                      <p className="text-sm text-slate-500">
                        ${plan.price}/month
                      </p>
                    </div>
                    <div
                      className={cn(
                        'h-5 w-5 rounded-full border-2',
                        formData.planId === plan.id
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-slate-300'
                      )}
                    >
                      {formData.planId === plan.id && (
                        <Check className="h-full w-full text-white p-0.5" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {errors.planId && (
                <p className="text-xs text-red-500">{errors.planId}</p>
              )}
            </div>
          )}

          {/* Step 3: Admin User */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500 mb-4">
                Create the initial admin user for this account.
              </p>

              <div className="space-y-2">
                <Label htmlFor="adminEmail">Email *</Label>
                <Input
                  id="adminEmail"
                  type="email"
                  placeholder="admin@acme.com"
                  value={formData.adminEmail}
                  onChange={(e) => handleChange('adminEmail', e.target.value)}
                  className={errors.adminEmail ? 'border-red-500' : ''}
                />
                {errors.adminEmail && (
                  <p className="text-xs text-red-500">{errors.adminEmail}</p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="adminFirstName">First Name *</Label>
                  <Input
                    id="adminFirstName"
                    placeholder="John"
                    value={formData.adminFirstName}
                    onChange={(e) => handleChange('adminFirstName', e.target.value)}
                    className={errors.adminFirstName ? 'border-red-500' : ''}
                  />
                  {errors.adminFirstName && (
                    <p className="text-xs text-red-500">{errors.adminFirstName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminLastName">Last Name *</Label>
                  <Input
                    id="adminLastName"
                    placeholder="Doe"
                    value={formData.adminLastName}
                    onChange={(e) => handleChange('adminLastName', e.target.value)}
                    className={errors.adminLastName ? 'border-red-500' : ''}
                  />
                  {errors.adminLastName && (
                    <p className="text-xs text-red-500">{errors.adminLastName}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="skipWelcomeEmail"
                  checked={formData.skipWelcomeEmail}
                  onChange={(e) => handleChange('skipWelcomeEmail', e.target.checked)}
                  className="rounded border-slate-300"
                />
                <Label htmlFor="skipWelcomeEmail" className="text-sm font-normal">
                  Skip welcome email
                </Label>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <p className="text-sm text-slate-500 mb-4">
                Review the account details before creating.
              </p>

              <div className="space-y-4">
                <ReviewSection title="Account">
                  <ReviewRow label="Name" value={formData.name} />
                  <ReviewRow label="Slug" value={`/${formData.slug}`} mono />
                  <ReviewRow label="Industry" value={formData.industry || '—'} />
                  <ReviewRow label="Source" value={formData.source} capitalize />
                </ReviewSection>

                <ReviewSection title="Plan">
                  <ReviewRow
                    label="Selected Plan"
                    value={selectedPlan ? `${selectedPlan.name} ($${selectedPlan.price}/mo)` : '—'}
                  />
                </ReviewSection>

                <ReviewSection title="Admin User">
                  <ReviewRow label="Email" value={formData.adminEmail} />
                  <ReviewRow
                    label="Name"
                    value={`${formData.adminFirstName} ${formData.adminLastName}`}
                  />
                  <ReviewRow
                    label="Welcome Email"
                    value={formData.skipWelcomeEmail ? 'Skipped' : 'Will be sent'}
                  />
                </ReviewSection>

                {formData.notes && (
                  <ReviewSection title="Notes">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {formData.notes}
                    </p>
                  </ReviewSection>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={currentStep === 1 ? () => navigate({ to: '/accounts' }) : handleBack}
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {currentStep === 1 ? 'Cancel' : 'Back'}
            </Button>

            {currentStep < 4 ? (
              <Button onClick={handleNext}>
                Next
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isPerformingAction}>
                {isPerformingAction ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="mr-1.5 h-4 w-4" />
                    Create Account
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ============================================
// Subcomponents
// ============================================

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border p-4">
      <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
        {title}
      </h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ReviewRow({
  label,
  value,
  mono,
  capitalize,
}: {
  label: string;
  value: string;
  mono?: boolean;
  capitalize?: boolean;
}) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span
        className={cn(
          'text-slate-900 dark:text-white',
          mono && 'font-mono',
          capitalize && 'capitalize'
        )}
      >
        {value}
      </span>
    </div>
  );
}
